/**
 * src/pages/ConfigEditor.js
 *
 * /config              — new version based on latest published
 * /config/history/:id  — view a specific version (read mode by default)
 *
 * Read mode:  structured read-only display of the config
 * Edit mode:  form editor, entered via "Modify" button
 *
 * Save behaviour depends on what is being edited:
 *   - Draft version  → PATCH in place (same version_number, no new row)
 *   - Published version or new → POST creates a new version numbered MAX+1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_CUSTOM_FIELD = () => ({ key: '', selector: '', attribute: 'value' });

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
];
const VALUE_OPERATORS = ['equals', 'not_equals', 'greater_than', 'less_than'];
const CONDITION_MODES = [
  { value: 'allow_when_true', label: 'Allow when true' },
  { value: 'suppress_when_true', label: 'Suppress when true' },
];
const EMPTY_CONDITION = () => ({ enabled: true, selector: '', attribute: 'value', operator: 'equals', value: '' });

const EMPTY_IDENTIFIER = () => ({ label: '', selector: '', attribute: 'value', required: false });

const EMPTY_STEP = () => ({
  step_id: '', label: '', application_name: '', url_pattern: '',
  enabled: true,
  trigger_selector: '', trigger_keyboard_selector: '',
  identifiers: [EMPTY_IDENTIFIER()],
  conditions_mode: 'allow_when_true', conditions: [],
  custom_fields: [],
  debug: false
});
const EMPTY_PROCESS = () => ({
  process_id: '', process_name: '', enabled: true, steps: [EMPTY_STEP()]
});

function configToForm(config) {
  if (!config) return [EMPTY_PROCESS()];
  return (config.processes || []).map(p => ({
    process_id: p.process_id || '',
    process_name: p.process_name || '',
    enabled: p.enabled !== false,
    steps: (p.steps || []).map(s => ({
      step_id: s.step_id || '',
      label: s.label || '',
      enabled: s.enabled !== false,
      application_name: s.application_name || '',
      url_pattern: s.url_pattern || '',
      trigger_selector: s.trigger?.selector || '',
      trigger_keyboard_selector: s.trigger?.keyboard_selector || '',
      identifiers: Object.entries(s.identifiers || {}).map(([label, def]) => ({
        label,
        selector: def.selector || '',
        attribute: def.attribute || 'value',
        required: def.required === true
      })),
      conditions_mode: s.conditions_mode || 'allow_when_true',
      conditions: (s.conditions || []).map(c => ({
        enabled: c.enabled !== false,
        selector: c.element?.selector || '',
        attribute: c.element?.attribute || 'value',
        operator: c.operator || 'equals',
        value: c.value || ''
      })),
      custom_fields: Object.entries(s.custom_fields || {}).map(([key, val]) => ({
        key, selector: val.selector || '', attribute: val.attribute || 'value'
      })),
      debug: s.debug === true
    }))
  }));
}

function formToConfig(processes, baseConfig) {
  return {
    processes: processes.map(p => ({
      process_id: p.process_id,
      process_name: p.process_name,
      enabled: p.enabled,
      steps: p.steps.map(s => ({
        step_id: s.step_id,
        label: s.label,
        enabled: s.enabled,
        application_name: s.application_name,
        url_pattern: s.url_pattern,
        trigger: {
          type: 'click',
          selector: s.trigger_selector,
          ...(s.trigger_keyboard_selector ? { keyboard_selector: s.trigger_keyboard_selector } : {})
        },
        identifiers: s.identifiers.reduce((acc, id) => {
          if (id.label) {
            acc[id.label] = { selector: id.selector, attribute: id.attribute, ...(id.required ? { required: true } : {}) };
          }
          return acc;
        }, {}),
        custom_fields: s.custom_fields.reduce((acc, cf) => {
          if (cf.key) acc[cf.key] = { type: 'dom', selector: cf.selector, attribute: cf.attribute };
          return acc;
        }, {}),
        ...(s.conditions.length > 0 ? {
          conditions_mode: s.conditions_mode,
          conditions: s.conditions.map(c => {
            const condObj = {
              enabled: c.enabled,
              element: { type: 'dom', selector: c.selector, attribute: c.attribute },
              operator: c.operator,
            };
            if (VALUE_OPERATORS.includes(c.operator)) {
              condObj.value = c.value;
            }
            return condObj;
          })
        } : {}),
        ...(s.debug ? { debug: true } : {})
      }))
    }))
  };
}

// ---------------------------------------------------------------------------
// Cancel dialog
// ---------------------------------------------------------------------------

function CancelDialog({ onSaveDraft, onSavePublish, onDiscard, onClose, saving }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="card" style={{ width: '420px', boxShadow: 'var(--shadow-lg)' }}>
        <div className="card-header">
          <span className="card-title">Unsaved Changes</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
            You have unsaved changes. What would you like to do?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button className="btn btn-primary" onClick={onSavePublish} disabled={saving}
              style={{ justifyContent: 'center' }}>
              Save & Publish
            </button>
            <button className="btn btn-secondary" onClick={onSaveDraft} disabled={saving}
              style={{ justifyContent: 'center' }}>
              Save as Draft
            </button>
            <button className="btn btn-danger" onClick={onDiscard}
              style={{ justifyContent: 'center' }}>
              Discard Changes
            </button>
            <button className="btn btn-ghost" onClick={onClose}
              style={{ justifyContent: 'center' }}>
              Continue Editing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read mode — structured display of a config version
// ---------------------------------------------------------------------------

function ReadView({ version, config, processes, onModify, isAdmin, onToggle, togglingPaths, error }) {
  return (
    <div className="page">
      {/* Breadcrumb */}
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Link to="/config/history" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          Version History
        </Link>
        <span>›</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>v{version.version_number}</span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">v{version.version_number}</h1>
          <p className="page-subtitle">
            Created by {version.created_by_email} on {new Date(version.created_at).toLocaleString()}
            {version.published_at && ` · Published ${new Date(version.published_at).toLocaleString()}`}
          </p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={onModify} disabled={!!error}>
            Modify
          </button>
        )}
      </div>

      {processes.some(p => p.steps.some(s => s.debug)) && (
        <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
          Debug mode is enabled on one or more steps in this version.
          Events from debug steps will include DOM snapshots and override condition suppression.
        </div>
      )}

      {processes.map((process, pi) => (
        <div className="card" key={pi} style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <span className="card-title">{process.process_name || `Process ${pi + 1}`}</span>
            <div className="flex items-center gap-8">
              <span className="font-mono text-sm text-secondary">{process.process_id}</span>
              {isAdmin && onToggle ? (
                <label className="toggle" style={{ marginBottom: 0 }}>
                  <input type="checkbox" checked={process.enabled}
                    onChange={() => onToggle(`processes[${pi}].enabled`, !process.enabled)}
                    disabled={togglingPaths?.has(`processes[${pi}].enabled`)} />
                  <div className="toggle-track" /><div className="toggle-thumb" />
                  <span className="toggle-label">{process.enabled ? 'Enabled' : 'Disabled'}</span>
                </label>
              ) : (
                <span className={`badge ${process.enabled ? 'badge-success' : 'badge-danger'}`}>
                  <span className="status-dot" />
                  {process.enabled ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {process.steps.map((step, si) => (
              <div key={si} style={{
                padding: '20px 24px',
                borderBottom: si < process.steps.length - 1 ? '1px solid var(--border)' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{step.label}</span>
                    {isAdmin && onToggle ? (
                      <>
                        <label className="toggle" style={{ marginBottom: 0 }}>
                          <input type="checkbox" checked={step.enabled}
                            onChange={() => onToggle(`processes[${pi}].steps[${si}].enabled`, !step.enabled)}
                            disabled={togglingPaths?.has(`processes[${pi}].steps[${si}].enabled`)} />
                          <div className="toggle-track" /><div className="toggle-thumb" />
                          <span className="toggle-label">{step.enabled ? 'Enabled' : 'Disabled'}</span>
                        </label>
                        <label className="toggle" style={{ marginBottom: 0, marginLeft: '4px' }}>
                          <input type="checkbox" checked={step.debug}
                            onChange={() => onToggle(`processes[${pi}].steps[${si}].debug`, !step.debug)}
                            disabled={togglingPaths?.has(`processes[${pi}].steps[${si}].debug`)} />
                          <div className="toggle-track" /><div className="toggle-thumb" />
                          <span className="toggle-label">Debug</span>
                        </label>
                      </>
                    ) : (
                      <>
                        {step.enabled === false && (
                          <span className="badge badge-danger">
                            <span className="status-dot" />Disabled
                          </span>
                        )}
                        {step.debug && (
                          <span className="badge badge-warning">
                            <span className="status-dot" />Debug
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <span className="font-mono text-sm text-secondary">{step.step_id}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  <ReadField label="Application" value={step.application_name} />
                  <ReadField label="URL Pattern" value={step.url_pattern} mono />
                  <ReadField label="Trigger Selector" value={step.trigger_selector} mono />
                  <ReadField label="Keyboard Selector" value={step.trigger_keyboard_selector} mono />
                </div>

                {step.identifiers && step.identifiers.length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Identifiers
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {step.identifiers.map((id, ii) => (
                        <span key={ii} style={{
                          background: 'var(--content-bg)', border: '1px solid var(--border)',
                          borderRadius: '4px', padding: '3px 8px',
                          fontSize: '12px', fontFamily: 'var(--font-mono)',
                          display: 'inline-flex', alignItems: 'center', gap: '6px'
                        }}>
                          {id.label} → {id.selector} [{id.attribute}]
                          {id.required && (
                            <span style={{
                              fontSize: '10px', fontWeight: '600', color: 'var(--warning)',
                              fontFamily: 'var(--font-sans)', textTransform: 'uppercase'
                            }}>Required</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {step.conditions.length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Conditions
                      <span style={{ fontWeight: '400', textTransform: 'none', marginLeft: '8px' }}>
                        ({CONDITION_MODES.find(m => m.value === step.conditions_mode)?.label || 'Allow when true'})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {step.conditions.map((c, ci) => (
                        <span key={ci} style={{
                          background: 'var(--content-bg)', border: '1px solid var(--border)',
                          borderRadius: '4px', padding: '3px 8px',
                          fontSize: '12px', fontFamily: 'var(--font-mono)',
                          opacity: c.enabled ? 1 : 0.5,
                          display: 'inline-flex', alignItems: 'center'
                        }}>
                          {isAdmin && onToggle ? (
                            <label className="toggle" style={{ marginBottom: 0, marginRight: '6px' }}>
                              <input type="checkbox" checked={c.enabled}
                                onChange={() => onToggle(`processes[${pi}].steps[${si}].conditions[${ci}].enabled`, !c.enabled)}
                                disabled={togglingPaths?.has(`processes[${pi}].steps[${si}].conditions[${ci}].enabled`)} />
                              <div className="toggle-track" /><div className="toggle-thumb" />
                            </label>
                          ) : (
                            <span style={{
                              display: 'inline-block', width: '6px', height: '6px',
                              borderRadius: '50%', marginRight: '6px',
                              background: c.enabled ? 'var(--success)' : 'var(--text-tertiary)'
                            }} />
                          )}
                          {c.selector} [{c.attribute}] {c.operator}{VALUE_OPERATORS.includes(c.operator) ? ` "${c.value}"` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {step.custom_fields.length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Custom Fields
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {step.custom_fields.map((cf, fi) => (
                        <span key={fi} style={{
                          background: 'var(--content-bg)', border: '1px solid var(--border)',
                          borderRadius: '4px', padding: '3px 8px',
                          fontSize: '12px', fontFamily: 'var(--font-mono)'
                        }}>
                          {cf.key} → {cf.selector} [{cf.attribute}]
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReadField({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        color: value ? 'var(--text-primary)' : 'var(--text-tertiary)'
      }}>
        {value || '—'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ConfigEditor() {
  const navigate = useNavigate();
  const { id: sourceVersionId } = useParams();
  const { user } = useAuth();

  const [sourceVersion, setSourceVersion] = useState(null);
  const [baseConfig, setBaseConfig] = useState(null);
  const [nextVersionNumber, setNextVersionNumber] = useState(null);
  const [processes, setProcesses] = useState([EMPTY_PROCESS()]);
  const [originalProcesses, setOriginalProcesses] = useState(null); // snapshot on entering edit mode
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [togglingPaths, setTogglingPaths] = useState(new Set());

  // If no sourceVersionId, we're creating a new version — go straight to edit mode
  const isNewVersion = !sourceVersionId;

  // True when the version being viewed/edited is an unpublished draft.
  // In this case saving patches the existing row instead of creating a new version.
  const isEditingDraft = !isNewVersion && !!sourceVersion && !sourceVersion.published_at;

  async function fetchConfig() {
    setError('');
    setLoading(true);
    try {
      const versionsRes = await api.get('/portal/config/versions');
      const allVersions = versionsRes.data;
      const maxVersion = allVersions.reduce((max, v) => Math.max(max, v.version_number), 0);
      setNextVersionNumber(maxVersion + 1);

      let sourceId = sourceVersionId;
      if (!sourceId) {
        const latestPublished = allVersions.find(v => v.published_at);
        sourceId = latestPublished?.id;
      }

      if (sourceId) {
        const versionRes = await api.get(`/portal/config/versions/${sourceId}`);
        setSourceVersion(versionRes.data);
        setBaseConfig(versionRes.data.config);
        const formData = configToForm(versionRes.data.config);
        setProcesses(formData);
      }
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load config.');
      }
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchConfig(); }, [sourceVersionId]);

  // Start editing — snapshot current processes for dirty check
  function handleModify() {
    setOriginalProcesses(JSON.stringify(processes));
    setIsEditing(true);
  }

  // Check if form has changed since entering edit mode
  const isDirty = useCallback(() => {
    if (!originalProcesses) return false;
    return JSON.stringify(processes) !== originalProcesses;
  }, [processes, originalProcesses]);

  // Cancel button pressed
  function handleCancelClick() {
    if (isDirty()) {
      setPendingNavigation(null); // null = go back to read view of current version
      setShowCancelDialog(true);
    } else {
      exitEditMode(null);
    }
  }

  function exitEditMode(destination) {
    // Restore form state to the snapshot taken when edit mode was entered,
    // so read mode shows the actual stored values rather than abandoned edits.
    if (originalProcesses) {
      setProcesses(JSON.parse(originalProcesses));
    }
    setIsEditing(false);
    setShowCancelDialog(false);
    setPendingNavigation(null);
    setError('');
    if (destination) {
      navigate(destination);
    } else if (isNewVersion) {
      navigate('/config/history');
    }
    // else: stay on read view of current version (isEditing = false handles this)
  }

  // Save handler
  async function handleSave(publish) {
    setError('');
    setSuccess('');
    setSaving(true);
    setShowCancelDialog(false);
    const config = formToConfig(processes, baseConfig);
    try {
      if (isEditingDraft) {
        // Patch the existing draft row in place — no new version created.
        await api.patch(`/portal/config/versions/${sourceVersionId}`, { config });
        if (publish) {
          await api.put(`/portal/config/versions/${sourceVersionId}/publish`);
          setSuccess(`v${sourceVersion.version_number} updated and published — extensions will pick it up on their next event.`);
        } else {
          setSuccess(`Draft v${sourceVersion.version_number} updated.`);
        }
      } else {
        // Create a new version (editing a published version or starting fresh).
        const res = await api.post('/portal/config/versions', { config });
        const newVersionId = res.data.id;
        const newVersionNumber = res.data.version_number;
        if (publish) {
          await api.put(`/portal/config/versions/${newVersionId}/publish`);
          setSuccess(`v${newVersionNumber} saved and published — extensions will pick it up on their next event.`);
        } else {
          setSuccess(`v${newVersionNumber} saved as draft.`);
        }
      }
      setTimeout(() => navigate('/config/history'), 1800);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to save configuration.');
      }
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Inline toggle handler — changes operational booleans in place on any version
  // ---------------------------------------------------------------------------
  async function handleToggle(path, value) {
    setError('');
    const previousProcesses = JSON.parse(JSON.stringify(processes));

    // Apply optimistically
    setTogglingPaths(prev => new Set([...prev, path]));
    const match = path.match(/^processes\[(\d+)\](?:\.steps\[(\d+)\](?:\.conditions\[(\d+)\])?)?\.(\w+)$/);
    if (match) {
      const [, pi, si, ci, field] = match;
      setProcesses(prev => {
        const next = JSON.parse(JSON.stringify(prev));
        if (ci !== undefined) {
          next[parseInt(pi)].steps[parseInt(si)].conditions[parseInt(ci)][field] = value;
        } else if (si !== undefined) {
          next[parseInt(pi)].steps[parseInt(si)][field] = value;
        } else {
          next[parseInt(pi)][field] = value;
        }
        return next;
      });
    }

    try {
      await api.patch(`/portal/config/versions/${sourceVersionId}/toggles`, {
        toggles: [{ path, value }]
      });
    } catch (err) {
      // Revert on failure
      setProcesses(previousProcesses);
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to update toggle.');
      }
    } finally {
      setTogglingPaths(prev => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Process / step / custom field operations
  // ---------------------------------------------------------------------------
  function addProcess() { setProcesses(prev => [...prev, EMPTY_PROCESS()]); }
  function removeProcess(pi) { setProcesses(prev => prev.filter((_, i) => i !== pi)); }
  function updateProcess(pi, field, value) {
    setProcesses(prev => prev.map((p, i) => i === pi ? { ...p, [field]: value } : p));
  }
  function addStep(pi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: [...p.steps, EMPTY_STEP()] } : p));
  }
  function removeStep(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.filter((_, j) => j !== si) } : p));
  }
  function updateStep(pi, si, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) => j === si ? { ...s, [field]: value } : s) } : p));
  }
  function addCustomField(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: [...s.custom_fields, EMPTY_CUSTOM_FIELD()] } : s) } : p));
  }
  function removeCustomField(pi, si, fi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.filter((_, k) => k !== fi) } : s) } : p));
  }
  function updateCustomField(pi, si, fi, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.map((cf, k) =>
          k === fi ? { ...cf, [field]: value } : cf) } : s) } : p));
  }
  function addIdentifier(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: [...s.identifiers, EMPTY_IDENTIFIER()] } : s) } : p));
  }
  function removeIdentifier(pi, si, ii) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.filter((_, k) => k !== ii) } : s) } : p));
  }
  function updateIdentifier(pi, si, ii, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.map((id, k) =>
          k === ii ? { ...id, [field]: value } : id) } : s) } : p));
  }
  function addCondition(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: [...s.conditions, EMPTY_CONDITION()] } : s) } : p));
  }
  function removeCondition(pi, si, ci) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.filter((_, k) => k !== ci) } : s) } : p));
  }
  function updateCondition(pi, si, ci, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.map((c, k) =>
          k === ci ? { ...c, [field]: value } : c) } : s) } : p));
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Load error with no data — show retry
  if (error && !sourceVersion && !isNewVersion) {
    return (
      <div className="page">
        <div className="alert alert-error">
          {error}
          <button className="alert-retry" onClick={fetchConfig}>Try again</button>
        </div>
      </div>
    );
  }

  // Read mode (only for existing versions)
  if (!isNewVersion && !isEditing) {
    return (
      <ReadView
        version={sourceVersion}
        config={baseConfig}
        processes={processes}
        onModify={handleModify}
        isAdmin={user?.role === 'admin'}
        onToggle={handleToggle}
        togglingPaths={togglingPaths}
        error={error}
      />
    );
  }

  // Edit mode
  const saveButtons = (
    <div className="flex gap-8">
      <button className="btn btn-ghost" onClick={handleCancelClick} disabled={saving}>
        Cancel
      </button>
      <button className="btn btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
        Save as Draft
      </button>
      <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
        {saving ? 'Saving…' : 'Save & Publish'}
      </button>
    </div>
  );

  return (
    <div className="page">
      {showCancelDialog && (
        <CancelDialog
          saving={saving}
          onSaveDraft={() => handleSave(false)}
          onSavePublish={() => handleSave(true)}
          onDiscard={() => exitEditMode(pendingNavigation)}
          onClose={() => setShowCancelDialog(false)}
        />
      )}

      {/* Breadcrumb */}
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Link to="/config/history" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
          Version History
        </Link>
        <span>›</span>
        {sourceVersion
          ? <><Link to={`/config/history/${sourceVersion.id}`} style={{ color: 'var(--accent)', textDecoration: 'none' }} onClick={e => {
                    e.preventDefault();
                    const dest = `/config/history/${sourceVersion.id}`;
                    if (isDirty()) {
                      setPendingNavigation(dest);
                      setShowCancelDialog(true);
                    } else {
                      setIsEditing(false);
                      navigate(dest);
                    }
                  }}>v{sourceVersion.version_number}</Link><span>›</span><span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>Edit</span></>
          : <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>New Version</span>
        }
      </div>

      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">
            {isEditingDraft
              ? `Edit Draft v${sourceVersion.version_number}`
              : sourceVersion
              ? `Edit based on v${sourceVersion.version_number}`
              : 'New Version'}
          </h1>
          <p className="page-subtitle">
            {isEditingDraft
              ? 'Changes will update this draft in place — no new version will be created.'
              : 'Changes are always saved as a new version.'}
          </p>
        </div>
        {saveButtons}
      </div>

      {/* Source version banner */}
      {sourceVersion && (
        <div style={{
          background: 'var(--accent-subtle)', border: '1px solid #c7d7fe',
          borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
          fontSize: '13px', color: '#3730a3', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <span style={{ fontSize: '15px' }}>ℹ️</span>
          {isEditingDraft
            ? <>Editing draft <strong>v{sourceVersion.version_number}</strong> — changes will update this version in place.</>
            : <>Based on <strong>v{sourceVersion.version_number}</strong>
                {sourceVersion.published_at
                  ? ` (published ${new Date(sourceVersion.published_at).toLocaleDateString()})`
                  : ' (draft)'}
                {nextVersionNumber && <> — saving will create <strong>v{nextVersionNumber}</strong>.</>}
              </>
          }
        </div>
      )}

      {processes.some(p => p.steps.some(s => s.debug)) && (
        <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
          Debug mode is enabled on one or more steps.
          Events from debug steps will include DOM snapshots and override condition suppression.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Processes */}
      {processes.map((process, pi) => (
        <div className="card" key={pi} style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <span className="card-title">
              Process {pi + 1}{process.process_name ? ` — ${process.process_name}` : ''}
            </span>
            <div className="flex items-center gap-12">
              <label className="toggle">
                <input type="checkbox" checked={process.enabled}
                  onChange={e => updateProcess(pi, 'enabled', e.target.checked)} />
                <div className="toggle-track" /><div className="toggle-thumb" />
                <span className="toggle-label">{process.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
              {processes.length > 1 && (
                <button className="btn btn-danger btn-sm" onClick={() => removeProcess(pi)}>Remove</button>
              )}
            </div>
          </div>

          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Process ID</label>
                <input className="form-input mono" value={process.process_id}
                  onChange={e => updateProcess(pi, 'process_id', e.target.value)}
                  placeholder="invoice-approval" />
                <p className="form-hint">Lowercase, hyphens only.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Process Name</label>
                <input className="form-input" value={process.process_name}
                  onChange={e => updateProcess(pi, 'process_name', e.target.value)}
                  placeholder="Invoice Approval Workflow" />
              </div>
            </div>

            {process.steps.map((step, si) => (
              <div key={si} style={{
                background: 'var(--content-bg)', border: '1px solid var(--border)',
                borderRadius: '8px', padding: '20px', marginBottom: '16px'
              }}>
                <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                    Step {si + 1}{step.label ? ` — ${step.label}` : ''}
                  </div>
                  <div className="flex items-center gap-12">
                    <label className="toggle" style={{ marginBottom: 0 }}>
                      <input type="checkbox" checked={step.enabled}
                        onChange={e => updateStep(pi, si, 'enabled', e.target.checked)} />
                      <div className="toggle-track" /><div className="toggle-thumb" />
                      <span className="toggle-label">{step.enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                    {process.steps.length > 1 && (
                      <button className="btn btn-ghost btn-sm text-danger" onClick={() => removeStep(pi, si)}>
                        Remove step
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Step ID</label>
                    <input className="form-input mono" value={step.step_id}
                      onChange={e => updateStep(pi, si, 'step_id', e.target.value)} placeholder="step-001" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Label</label>
                    <input className="form-input" value={step.label}
                      onChange={e => updateStep(pi, si, 'label', e.target.value)} placeholder="Invoice Submitted" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Application Name</label>
                    <input className="form-input" value={step.application_name}
                      onChange={e => updateStep(pi, si, 'application_name', e.target.value)} placeholder="Acme ERP" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL Pattern</label>
                    <input className="form-input mono" value={step.url_pattern}
                      onChange={e => updateStep(pi, si, 'url_pattern', e.target.value)}
                      placeholder="https://app.example.com/*" />
                    <p className="form-hint">Use * as wildcard.</p>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Trigger CSS Selector</label>
                    <input className="form-input mono" value={step.trigger_selector}
                      onChange={e => updateStep(pi, si, 'trigger_selector', e.target.value)}
                      placeholder="button#submit-invoice" />
                    <p className="form-hint">CSS selector for the element to track clicks on.</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Keyboard Selector <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional)</span></label>
                    <input className="form-input mono" value={step.trigger_keyboard_selector}
                      onChange={e => updateStep(pi, si, 'trigger_keyboard_selector', e.target.value)}
                      placeholder="input#search" />
                    <p className="form-hint">CSS selector for an input that also triggers this step on Enter.</p>
                  </div>
                </div>

                <hr className="divider" style={{ margin: '16px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Identifiers
                  </div>
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '2px 8px' }}
                    onClick={() => addIdentifier(pi, si)}>+ Add Identifier</button>
                </div>

                {step.identifiers.map((id, ii) => (
                  <div key={ii} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr auto auto',
                    gap: '8px', alignItems: 'end', marginBottom: '8px'
                  }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      {ii === 0 && <label className="form-label">Label</label>}
                      <input className="form-input mono" value={id.label}
                        onChange={e => updateIdentifier(pi, si, ii, 'label', e.target.value)}
                        placeholder="invoice_number" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      {ii === 0 && <label className="form-label">Selector</label>}
                      <input className="form-input mono" value={id.selector}
                        onChange={e => updateIdentifier(pi, si, ii, 'selector', e.target.value)}
                        placeholder="#invoice-number" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      {ii === 0 && <label className="form-label">Attribute</label>}
                      <input className="form-input mono" value={id.attribute}
                        onChange={e => updateIdentifier(pi, si, ii, 'attribute', e.target.value)}
                        placeholder="value" />
                    </div>
                    <label className="toggle" style={{ marginBottom: ii === 0 ? 0 : 0, paddingBottom: '6px' }}>
                      {ii === 0 && <span className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Req.</span>}
                      <input type="checkbox" checked={id.required}
                        onChange={e => updateIdentifier(pi, si, ii, 'required', e.target.checked)} />
                      <div className="toggle-track" /><div className="toggle-thumb" />
                    </label>
                    <button type="button" className="btn btn-ghost" onClick={() => removeIdentifier(pi, si, ii)}
                      disabled={step.identifiers.length <= 1}
                      style={{ padding: '6px', fontSize: '16px', lineHeight: 1, color: 'var(--danger)', marginBottom: ii === 0 ? 0 : 0 }}
                      title="Remove identifier">×</button>
                  </div>
                ))}
                <p className="form-hint" style={{ marginTop: '4px' }}>
                  Steps sharing a label (e.g. "invoice_number") are linked by matching values.
                  Use <code>value</code> for inputs, <code>innerText</code> for text elements.
                </p>

                <hr className="divider" style={{ margin: '16px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Conditions
                  </div>
                  {step.conditions.length > 0 && (
                    <select className="form-select" value={step.conditions_mode}
                      onChange={e => updateStep(pi, si, 'conditions_mode', e.target.value)}
                      style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}>
                      {CONDITION_MODES.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  )}
                </div>

                {step.conditions.map((cond, ci) => (
                  <div key={ci} style={{
                    display: 'grid',
                    gridTemplateColumns: VALUE_OPERATORS.includes(cond.operator)
                      ? 'auto 1fr 1fr 1fr 1fr auto'
                      : 'auto 1fr 1fr 1fr auto',
                    gap: '8px', alignItems: 'end', marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '4px' }}>
                      {ci === 0 && <label className="form-label" style={{ visibility: 'hidden' }}>&nbsp;</label>}
                      <label className="toggle" style={{ marginBottom: 0 }}>
                        <input type="checkbox" checked={cond.enabled}
                          onChange={e => updateCondition(pi, si, ci, 'enabled', e.target.checked)} />
                        <div className="toggle-track" /><div className="toggle-thumb" />
                      </label>
                    </div>
                    <div>
                      {ci === 0 && <label className="form-label">Selector</label>}
                      <input className="form-input mono" value={cond.selector}
                        onChange={e => updateCondition(pi, si, ci, 'selector', e.target.value)}
                        placeholder="#status-field" disabled={!cond.enabled} />
                    </div>
                    <div>
                      {ci === 0 && <label className="form-label">Attribute</label>}
                      <input className="form-input mono" value={cond.attribute}
                        onChange={e => updateCondition(pi, si, ci, 'attribute', e.target.value)}
                        placeholder="value" disabled={!cond.enabled} />
                    </div>
                    <div>
                      {ci === 0 && <label className="form-label">Operator</label>}
                      <select className="form-select" value={cond.operator}
                        disabled={!cond.enabled}
                        onChange={e => {
                          updateCondition(pi, si, ci, 'operator', e.target.value);
                          if (!VALUE_OPERATORS.includes(e.target.value)) {
                            updateCondition(pi, si, ci, 'value', '');
                          }
                        }}>
                        {CONDITION_OPERATORS.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                    </div>
                    {VALUE_OPERATORS.includes(cond.operator) && (
                      <div>
                        {ci === 0 && <label className="form-label">Value</label>}
                        <input className="form-input mono" value={cond.value}
                          onChange={e => updateCondition(pi, si, ci, 'value', e.target.value)}
                          placeholder="expected value" disabled={!cond.enabled} />
                      </div>
                    )}
                    <button className="btn btn-ghost btn-sm text-danger"
                      onClick={() => removeCondition(pi, si, ci)}>✕</button>
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" onClick={() => addCondition(pi, si)}
                  style={{ marginTop: step.conditions.length > 0 ? '4px' : '0' }}>
                  + Add condition
                </button>

                <hr className="divider" style={{ margin: '16px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Debug Mode
                    </div>
                    <p className="form-hint" style={{ marginTop: '4px', marginBottom: 0 }}>
                      When enabled, conditions are overridden and events are always sent with a DOM snapshot.
                    </p>
                  </div>
                  <label className="toggle" style={{ marginBottom: 0 }}>
                    <input type="checkbox" checked={step.debug}
                      onChange={e => updateStep(pi, si, 'debug', e.target.checked)} />
                    <div className="toggle-track" /><div className="toggle-thumb" />
                    <span className="toggle-label">{step.debug ? 'On' : 'Off'}</span>
                  </label>
                </div>

                {step.custom_fields.length > 0 && (
                  <>
                    <hr className="divider" style={{ margin: '16px 0' }} />
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                      Custom Fields
                    </div>
                    {step.custom_fields.map((cf, fi) => (
                      <div key={fi} style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto',
                        gap: '8px', alignItems: 'end', marginBottom: '8px'
                      }}>
                        <div>
                          {fi === 0 && <label className="form-label">Field Name</label>}
                          <input className="form-input mono" value={cf.key}
                            onChange={e => updateCustomField(pi, si, fi, 'key', e.target.value)}
                            placeholder="department" />
                        </div>
                        <div>
                          {fi === 0 && <label className="form-label">Selector</label>}
                          <input className="form-input mono" value={cf.selector}
                            onChange={e => updateCustomField(pi, si, fi, 'selector', e.target.value)}
                            placeholder="#dept-code" />
                        </div>
                        <div>
                          {fi === 0 && <label className="form-label">Attribute</label>}
                          <input className="form-input mono" value={cf.attribute}
                            onChange={e => updateCustomField(pi, si, fi, 'attribute', e.target.value)}
                            placeholder="value" />
                        </div>
                        <button className="btn btn-ghost btn-sm text-danger"
                          onClick={() => removeCustomField(pi, si, fi)}>✕</button>
                      </div>
                    ))}
                  </>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => addCustomField(pi, si)}
                  style={{ marginTop: '8px' }}>
                  + Add custom field
                </button>
              </div>
            ))}

            <button className="btn btn-secondary btn-sm" onClick={() => addStep(pi)}>
              + Add step
            </button>
          </div>
        </div>
      ))}

      <button className="btn btn-secondary" onClick={addProcess}>+ Add process</button>

      {/* Sticky bottom save bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        background: 'rgba(247, 248, 250, 0.95)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid var(--border)',
        padding: '16px 0', marginTop: '32px',
        display: 'flex', justifyContent: 'flex-end', gap: '8px'
      }}>
        {saveButtons}
      </div>
    </div>
  );
}